import { useEffect, useRef, useState } from "react";
import type { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import {
  consumeSuppressSignOut,
  consumeUserInitiatedSignOut,
  isOfflineNow,
  markNeedsReauthExternal,
  refreshSessionAndSync,
} from "@/lib/offline/offline-auth";
import { getReachabilitySnapshot } from "@/lib/connectivity/reachability";
import { isUnlockedThisLaunch, isNative } from "@/lib/native";

export type AuthState = {
  session: Session | null;
  user: User | null;
  loading: boolean;
};

export function useAuth(): AuthState {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const restoredRef = useRef(false);

  useEffect(() => {
    let cancelled = false;

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, s) => {
      // SIGNED_OUT suppression. Supabase JS emits SIGNED_OUT in three cases
      // we care about:
      //   1. Our own refreshSessionAndSync() failed with invalid_grant
      //      (handled by consumeSuppressSignOut — armed inside that call).
      //   2. Supabase's autonomous background autoRefreshToken cycle failed
      //      (NOT armed by us — slips through and bounces the user to /login).
      //   3. The user tapped a real Sign Out button (must propagate).
      //
      // Rule: if a SIGNED_OUT arrives that the user did NOT initiate AND we
      // have any signal we're offline / backend-unreachable / already
      // unlocked this launch, keep the in-memory session and surface the
      // soft "Sign-in expired" toast instead of nuking the app mid-trip.
      if (event === "SIGNED_OUT") {
        const userInitiated = consumeUserInitiatedSignOut();
        if (!userInitiated) {
          const ownRefresh = consumeSuppressSignOut();
          const offline = isOfflineNow();
          const reach = getReachabilitySnapshot();
          const backendDown = reach.backendReachable === false;
          const unlocked = isUnlockedThisLaunch();
          if (ownRefresh || offline || backendDown || unlocked) {
            // eslint-disable-next-line no-console
            console.warn(
              `[auth] SIGNED_OUT suppressed (offline=${offline} backendDown=${backendDown} unlocked=${unlocked} ownRefresh=${ownRefresh}) — keeping local session`,
            );
            markNeedsReauthExternal();
            if (!cancelled && restoredRef.current) setLoading(false);
            return;
          }
        }
      }
      if (cancelled) return;
      setSession(s);
      // Keep loading=true until getSession() has restored from storage once.
      // This avoids protected-route churn while auth is still bootstrapping.
      if (restoredRef.current) setLoading(false);
    });

    void supabase.auth.getSession()
      .then(({ data }) => {
        if (cancelled) return;
        restoredRef.current = true;
        setSession(data.session);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        restoredRef.current = true;
        setSession(null);
        setLoading(false);
      });

    // Refresh on confirmed backend reachability — NOT on the raw `online`
    // event, and NOT before Face ID has unlocked this launch. Running it
    // before unlock can race with BiometricGate: a failed refresh tears
    // down the auth layout and the gate never gets a chance to render.
    let pendingReachable = false;
    const tryRefresh = () => {
      // Only gate on biometric unlock when running natively. On web there
      // is no BiometricGate and isUnlockedThisLaunch() is always false, so
      // we'd never refresh.
      if (isNative() && !isUnlockedThisLaunch()) {
        pendingReachable = true;
        return;
      }
      void refreshSessionAndSync();
    };

    const onReachable = () => { tryRefresh(); };
    const onUnlocked = () => {
      if (pendingReachable) {
        pendingReachable = false;
        void refreshSessionAndSync();
      }
    };
    if (typeof window !== "undefined") {
      window.addEventListener("roava:backend-reachable", onReachable);
      window.addEventListener("roava:biometric-unlocked", onUnlocked);
    }

    return () => {
      cancelled = true;
      subscription.unsubscribe();
      if (typeof window !== "undefined") {
        window.removeEventListener("roava:backend-reachable", onReachable);
        window.removeEventListener("roava:biometric-unlocked", onUnlocked);
      }
    };
  }, []);

  return { session, user: session?.user ?? null, loading };
}
